/**
 * Dashboard constants - Single source of truth
 * All strings centralized for future i18n support
 */

// --- Taglines (section separators) ---
export const TAGLINES = {
  marketplace: {
    fr: { slogan: 'Connect. Integrate. Accelerate.', description: 'La Marketplace qui connecte les acteurs de l\'automobile et accélère votre chiffre d\'affaires' },
    en: { slogan: 'Connect. Integrate. Accelerate.', description: 'The Marketplace that connects automotive industry players and accelerates your revenue' },
    nl: { slogan: 'Connect. Integrate. Accelerate.', description: 'De Marketplace die spelers in de auto-industrie verbindt en uw omzet versnelt' },
  },
  social: {
    fr: { slogan: 'Create. Publish. Grow.', description: 'Du contenu IA à la publication automatique, boostez votre présence sur les réseaux' },
    en: { slogan: 'Create. Publish. Grow.', description: 'From AI content to automatic publishing, boost your social media presence' },
    nl: { slogan: 'Create. Publish. Grow.', description: 'Van AI-content tot automatische publicatie, versterk uw aanwezigheid op sociale media' },
  },
  vitrine: {
    fr: { slogan: 'Display. Attract. Convert.', description: 'Votre vitrine digitale, visible partout — même sur les moteurs de recherche IA' },
    en: { slogan: 'Display. Attract. Convert.', description: 'Your digital showcase, visible everywhere — even on AI search engines' },
    nl: { slogan: 'Display. Attract. Convert.', description: 'Uw digitale etalage, overal zichtbaar — zelfs op AI-zoekmachines' },
  },
};

// Backward compat
export const TAGLINE = TAGLINES.marketplace;

// --- Personalized Greetings ---
export const GREETINGS = {
  fr: {
    morning: [
      'Prêt à faire tourner le business, {name} ?',
      'Les moteurs sont chauds, {name} ! C\'est parti',
      'Nouvelle journée, nouvelles opportunités, {name}',
      'Café en main, deals en vue, {name} ?',
      'La route est dégagée, {name}. À vous de jouer !',
    ],
    afternoon: [
      'On garde le cap, {name} !',
      'L\'après-midi s\'annonce productif, {name}',
      'Mi-journée, plein régime, {name} !',
      'Encore de belles affaires devant vous, {name}',
      'Le meilleur de la journée reste à venir, {name}',
    ],
    evening: [
      'Belle fin de journée, {name} !',
      'Un dernier coup d\'œil avant de décrocher, {name} ?',
      'Les affaires ne dorment jamais, {name}',
      'Encore un deal avant le coucher, {name} ?',
      'Beau travail aujourd\'hui, {name} !',
    ],
    night: [
      'Les meilleurs deals se préparent la nuit, {name}',
      'Toujours sur le pont, {name} ? Chapeau !',
      'Le business ne dort jamais — et vous non plus, {name}',
      'Mode nocturne activé, {name}. On avance !',
    ],
    subtitle: {
      pendingActions: 'Vous avez {count} action(s) en attente — on s\'en occupe ?',
      newUser: 'Bienvenue dans la famille ! Découvrez tout ce que Carmin peut faire pour vous',
      allClear: 'Tout est en ordre — continuez sur cette lancée !',
    },
  },
  en: {
    morning: [
      'Ready to roll, {name}?',
      'Engines are warm, {name}! Let\'s go',
      'New day, new deals, {name}',
      'Coffee\'s ready, deals are waiting, {name}',
      'The road is clear, {name}. Your move!',
    ],
    afternoon: [
      'Keeping up the momentum, {name}!',
      'Productive afternoon ahead, {name}',
      'Midday, full throttle, {name}!',
      'Great opportunities still ahead, {name}',
      'The best part of the day is yet to come, {name}',
    ],
    evening: [
      'Wrapping up nicely, {name}!',
      'One last look before signing off, {name}?',
      'Business never sleeps, {name}',
      'One more deal before the day ends, {name}?',
      'Great work today, {name}!',
    ],
    night: [
      'The best deals are made at night, {name}',
      'Still at it, {name}? Respect!',
      'Night owl mode — let\'s make it count, {name}',
      'Burning the midnight oil, {name}. Let\'s go!',
    ],
    subtitle: {
      pendingActions: 'You have {count} pending action(s) — shall we handle them?',
      newUser: 'Welcome to the family! Discover everything Carmin can do for you',
    },
  },
  nl: {
    morning: [
      'Klaar om zaken te doen, {name}?',
      'De motoren draaien warm, {name}! Laten we gaan',
      'Nieuwe dag, nieuwe kansen, {name}',
      'Koffie klaar, deals wachten, {name}?',
      'De weg is vrij, {name}. Aan u de beurt!',
    ],
    afternoon: [
      'We houden het tempo erin, {name}!',
      'Productieve middag in het verschiet, {name}',
      'Halverwege de dag, volle kracht vooruit, {name}!',
      'Er liggen nog mooie deals voor u klaar, {name}',
      'Het beste deel van de dag moet nog komen, {name}',
    ],
    evening: [
      'Mooie avond, {name}!',
      'Nog een laatste blik voor u stopt, {name}?',
      'Zaken slapen nooit, {name}',
      'Nog één deal voor het einde van de dag, {name}?',
      'Goed werk vandaag, {name}!',
    ],
    night: [
      'De beste deals worden \'s nachts gesloten, {name}',
      'Nog steeds bezig, {name}? Petje af!',
      'Nachtmodus aan, {name}. We gaan door!',
    ],
    subtitle: {
      pendingActions: 'U heeft {count} actie(s) in afwachting — zullen we ze aanpakken?',
      newUser: 'Welkom bij de familie! Ontdek alles wat Carmin voor u kan doen',
    },
  },
};

// --- Greeting helper ---
export const getGreeting = (name, locale = 'fr', pendingCount = 0, isNewUser = false) => {
  const lang = GREETINGS[locale] || GREETINGS.fr;
  const hour = new Date().getHours();

  let pool;
  if (hour >= 6 && hour < 12) pool = lang.morning;
  else if (hour >= 12 && hour < 18) pool = lang.afternoon;
  else if (hour >= 18 && hour < 22) pool = lang.evening;
  else pool = lang.night;

  // Use date as seed so greeting stays consistent during the day
  const today = new Date();
  const seed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
  const index = seed % pool.length;
  const greeting = pool[index].replace('{name}', name || '');

  let subtitle;
  if (isNewUser) {
    subtitle = lang.subtitle.newUser;
  } else if (pendingCount > 0) {
    subtitle = lang.subtitle.pendingActions.replace('{count}', pendingCount);
  } else {
    subtitle = lang.subtitle.allClear;
  }

  return { greeting, subtitle };
};

// --- Labels (i18n-ready) ---
export const LABELS = {
  // Header
  dashboard: 'Dashboard',
  addVehicle: 'Ajouter',

  // KPIs
  kpiStock: 'En stock',
  kpiMarketplace: 'Marketplace',
  kpiSocial: 'Réseaux',
  kpiVitrine: 'Vitrine',
  available: 'dispo',
  reserved: 'réservé',
  reservedPlural: 'réservés',
  offersSlashRdv: 'offres / rdv',
  activeListingsLabel: 'annonces actives',
  toPublish: 'à publier',
  scheduled: 'programmées',
  inShowcase: 'en vitrine',
  noScheduled: 'Aucune',

  // KPI detail lines
  kpiAvailable: 'disponibles',
  kpiReservedSingular: 'réservé',
  kpiReservedPlural: 'réservés',
  kpiInPreparation: 'en préparation',
  kpiAnnoncesActive: 'annonce active',
  kpiAnnoncesActivePlural: 'annonces actives',
  kpiOffresSingular: 'offre en attente',
  kpiOffresPlural: 'offres en attente',
  kpiRdv: 'rendez-vous',
  kpiNoActivity: 'Aucune activité',
  kpiReadyToPublish: 'prêtes à publier',
  kpiScheduled: 'programmées',
  kpiVitrineOnline: 'véhicules en ligne',
  kpiVitrineOff: 'Non configurée',

  // Section titles
  sectionMarketplace: 'Marketplace Pro',
  sectionSocial: 'Réseaux sociaux',
  sectionVitrine: 'Vitrine digitale',

  // Marketplace sub-sections
  suggestionsForYou: 'Suggestions pour vous',
  latestListings: 'Dernières annonces',
  activeNegotiations: 'Négociations actives',
  upcomingAppointments: 'Prochains rendez-vous',
  myListings: 'Mes annonces',

  // Social sub-sections
  readyToPublish: 'À publier',
  scheduledPubs: 'Programmées',
  recentActivity: 'Activité récente',

  // Social tabs
  tabPublish: 'À publier',
  tabScheduled: 'Programmées',
  tabHistory: 'Historique',
  noScheduledPubs: 'Aucune publication programmée',

  // Actions
  seeAll: 'Voir tout',
  viewLess: 'Réduire',
  accept: 'Accepter',
  counterOffer: 'Contre-offre',
  reject: 'Refuser',
  confirm: 'Confirmer',
  suggestOtherDate: 'Autre date',
  makeOffer: 'Offre',
  bookAppointment: 'RDV',
  respond: 'Répondre',
  validate: 'Valider',
  view: 'Voir',
  configure: 'Configurer',

  // Onboarding
  onboardingTitle: 'Bienvenue sur Carmin !',
  onboardingSubtitle: 'Complétez ces étapes pour profiter de toutes les fonctionnalités',
  stepConnectSocial: 'Connecter vos réseaux sociaux',
  stepConnectSocialDesc: 'Instagram et Facebook pour publier automatiquement',
  stepAddVehicle: 'Ajouter votre premier véhicule',
  stepAddVehicleDesc: 'Importez depuis AutoScout24 ou ajoutez manuellement',
  stepListMarketplace: 'Lister sur la Marketplace',
  stepListMarketplaceDesc: 'Vendez en B2B à d\'autres professionnels',
  stepSetupVitrine: 'Configurer votre vitrine',
  stepSetupVitrineDesc: 'Créez votre catalogue en ligne',

  // Offer status
  statusPending: 'En attente',
  statusCountered: 'Contre-offre',
  statusAccepted: 'Acceptée',
  statusRejected: 'Refusée',
  statusExpired: 'Expirée',
  statusCancelled: 'Annulée',

  // Appointment status
  appointmentPending: 'À confirmer',
  appointmentConfirmed: 'Confirmé',
  appointmentCompleted: 'Terminé',
  appointmentRejected: 'Refusé',
  appointmentCancelled: 'Annulé',

  // Suggestions
  basedOn: 'Basé sur',
  newListing: 'Nouvelle annonce',

  // Empty states
  noSuggestions: 'Aucune suggestion pour le moment',
  noSuggestionsDesc: 'Sauvegardez des recherches sur la Marketplace pour recevoir des suggestions personnalisées',
  noNegotiations: 'Aucune négociation en cours',
  noAppointments: 'Aucun rendez-vous à venir',
  noPublications: 'Aucune publication',
  nothingToPublish: 'Aucune annonce prête',
  nothingToPublishDesc: 'Ajoutez des véhicules et générez leur contenu',

  // Website page
  websiteTitle: 'Ma vitrine digitale',
  websiteStats: 'Statistiques des 30 derniers jours',
  websiteViewSite: 'Voir le site',
  websiteConfigure: 'Configurer',
  websiteNoTraffic: 'Aucun trafic pour le moment',
  websiteNoTrafficDesc: 'Votre vitrine est configurée et prête à recevoir des visiteurs.',
  websiteWidgetViews: 'Vues widget',
  websiteVehicleClicks: 'Clics véhicules',
  websiteContacts: 'Contacts',
  websiteCTR: 'Taux de clic (CTR)',
  websiteWeeklyTrend: 'Tendance hebdomadaire',
  websiteTrafficSources: 'Sources de trafic',
  websiteTopVehicles: 'Top véhicules cliqués',

  // Vitrine
  vitrineNotConfigured: 'Vitrine non configurée',
  vitrineNotConfiguredDesc: 'Configurez votre vitrine pour afficher vos véhicules en ligne',
  vitrineActive: 'Vitrine en ligne',
  vitrineVehicles: 'véhicules affichés',

  // Enhanced empty states
  suggestionsEmptyTitle: 'Trouvez votre prochain deal en un clic',
  suggestionsEmptyDesc: 'Explorez la Marketplace ou créez une alerte pour être notifié des nouveaux véhicules',
  suggestionsEmptyCta: 'Explorer la Marketplace',
  socialEmptyTitle: 'Vos réseaux sociaux n\'attendent que vous',
  socialEmptyDesc: 'Ajoutez des véhicules et générez du contenu IA pour publier en un clic',
  socialEmptyCta: 'Préparer une publication',
  vitrineEmptyTitle: 'Votre vitrine, votre image',
  vitrineEmptyDesc: 'Créez votre catalogue en ligne et rendez vos véhicules visibles partout, même sur les moteurs IA',
  vitrineEmptyCta: 'Configurer ma vitrine',

  // Hero section
  heroTitle: 'Bienvenue dans la famille, {name} !',
  heroSubtitle: 'Découvrez tout ce que Carmin peut faire pour vous',
  heroValueSpeed: 'Gagnez du temps',
  heroValueSpeedDesc: 'Publiez sur tous vos réseaux en un clic',
  heroValueNetwork: 'Réseau B2B exclusif',
  heroValueNetworkDesc: 'Achetez et vendez entre professionnels',
  heroValueVisibility: 'Visibilité maximale',
  heroValueVisibilityDesc: 'Vitrine en ligne, IA et réseaux sociaux',
  heroPillarsTitle: 'Tout ce dont vous avez besoin',
  heroPillarStock: 'Gestion du stock',
  heroPillarStockDesc: 'Importez, organisez et suivez votre inventaire',
  heroPillarMarketplace: 'Marketplace Pro',
  heroPillarMarketplaceDesc: 'Négociez et concluez vos deals B2B',
  heroPillarSocial: 'Réseaux sociaux',
  heroPillarSocialDesc: 'Contenu IA et publication automatique',
  heroPillarVitrine: 'Vitrine digitale',
  heroPillarVitrineDesc: 'Votre catalogue visible partout sur le web',
  heroChecklistTitle: 'Démarrage rapide',
  heroChecklistSubtitle: 'Quelques étapes pour tout activer',

  // Misc
  active: 'active',
  activePlural: 'actives',
  paused: 'en pause',
  round: 'Round',
  at: 'à',
  readyLabel: 'prêtes',
  plannedLabel: 'Publications planifiées',
  contentToGenerate: 'contenu',
  visitRequest: 'Demande de visite',
  offerOf: 'Offre de',
  counterOfferReceived: 'Contre-offre reçue',
  imagesLabel: 'Images',
  contentLabel: 'Contenu',
  needPhotos: 'Photos',
  needGenerate: 'Générer',
  actionsRequired: 'Actions requises',
  loadError: 'Erreur lors du chargement des données',
  filterAll: 'Tout',
  story: 'Story',
  feed: 'Feed',
  post: 'Post',

  // Marketplace tabs
  tabForYou: 'Pour vous',
  tabNegotiations: 'Négociations',
  tabAppointments: 'RDV',
  tabMyListings: 'Mes annonces',
  aiSummaryTitle: 'Résumé pour vous',
  savedAlerts: 'Vos alertes',
  exploreMarketplace: 'Explorer la Marketplace',
  noListingsYet: 'Aucune annonce pour le moment',
  listFromStock: 'Lister depuis votre stock',

  // Priority Alerts
  sectionAlerts: 'Alertes prioritaires',
  alertNewOffer: 'Nouvelle offre',
  alertOfferOn: 'sur',
  alertContactRequests: 'demandes de contact',
  alertPriceDropped: 'prix marché en baisse',
  alertOldStock: 'en stock',
  alertToWatch: 'à surveiller',
  alertAppointmentToConfirm: 'RDV à confirmer',
  alertAllClear: 'Tout est en ordre',
  alertAllClearDesc: 'Aucune alerte pour le moment — votre stock est bien géré',
  alertSeeAll: 'Voir toutes les notifications',

  // Notification Center
  notifAlerts: 'Alertes',
  notifRecommendations: 'Recommandations',
  notifDormantStock: 'dormant',
  notifToWatch: 'à surveiller (21-45j)',
  notifMissingPhotos: 'sans photos',
  notifOverpriced: 'au-dessus du marché',

  // Stock Overview
  sectionStockHealth: 'Santé du stock',
  sectionPricePosition: 'Positionnement prix',
  stockHealthy: 'En forme',
  stockToWatch: 'À surveiller',
  stockDormant: 'Dormant',
  stockAvgAge: 'Âge moyen stock',
  stockAvgRotation: 'Rotation moy.',
  stockDays: 'jours',
  stockVehiclesInStock: 'véhicules en stock',
  stockSeeDetail: 'Voir le détail stock',
  stockVsMarket: 'Votre stock vs le marché',
  stockUnderpriced: 'Sous-coté',
  stockGoodPrice: 'Bon',
  stockAboveMarket: 'Au-dessus',
  stockOverpriced: 'Sur-coté',
  stockVehiclesToAdjust: 'véhicules à ajuster',
  stockSeeRecommendations: 'Voir recommandations',

  // Stock Table (Top Opportunités)
  sectionTopOpportunities: 'Top opportunités',
  tableVehicle: 'Véhicule',
  tableMyPrice: 'Mon prix',
  tableMarket: 'Marché',
  tableGap: 'Écart',
  tableStock: 'Stock',
  tableStatus: 'Statut',
  statusOverpriced: 'Sur-coté',
  statusToAdjust: 'À ajuster',
  statusWellPriced: 'Bien placé',
  statusHighDemand: 'Forte dem.',
  stockTableSeeAll: 'Voir toute l\'analyse stock',
  stockTableAdjust: 'Ajuster',

  // Online Visibility
  sectionVisibility: 'Visibilité en ligne',
  visibilityScore: 'Score global',
  visibilityPlatform: 'Plateforme',
  visibilityListings: 'Annonces',
  visibilityViews: 'Vues',
  visibilityNotListed: 'annonces non diffusées',
  visibilityTotalViews: 'Vues totales 7j',
  visibilityContacts: 'Contacts 7j',
  visibilityContactRate: 'Taux contact',
  visibilityManage: 'Gérer mes diffusions',
  visibilitySiteWeb: 'Site web',
  visibilityExternalPlatforms: 'Annonces externes',
  visibilityOtherChannels: 'Autres canaux',
  visibilityNotConnected: 'Non connecté',
  visibilitySocialNetworks: 'Réseaux sociaux',

  // Recent Activity
  sectionRecentActivity: 'Activité récente',
  activityToday: 'Aujourd\'hui',
  activityYesterday: 'Hier',
  activityOfferReceived: 'Offre reçue',
  activityNewMessage: 'Nouveau message',
  activityAppointmentConfirmed: 'RDV confirmé',
  activitySaleCompleted: 'Vente conclue',
  activityPublished: 'Publié',
  activityScheduled: 'Programmé',
  activitySeeAll: 'Voir tout',

  // Dashboard header
  lastUpdate: 'Dernière MàJ',
  minutesAgo: 'min',

  // Quick actions
  qaAddVehicle: 'Ajouter un véhicule',
  qaAddVehicleDesc: 'Import ou saisie manuelle',
  qaPublish: 'Publier sur les réseaux',
  qaPublishDesc: 'Instagram & Facebook',
  qaExploreMarketplace: 'Explorer la Marketplace',
  qaExploreMarketplaceDesc: 'Recherches & alertes',
  qaVitrine: 'Ma vitrine digitale',
  qaVitrineDesc: 'Catalogue en ligne',

  // Actions banner
  pendingOffersSingular: 'offre en attente',
  pendingOffersPlural: 'offres en attente',
  pendingAppointmentsSingular: 'rendez-vous à confirmer',
  pendingAppointmentsPlural: 'rendez-vous à confirmer',
  viewOffers: 'Voir les offres',
  viewAppointments: 'Voir les RDV',
};

// --- Dashboard config ---
export const DASHBOARD_CONFIG = {
  maxSuggestions: 10,
  maxRecentActivity: 4,
  maxReadyVehicles: 5,
  suggestionsPerSearch: 3,
  initialVisibleItems: 5,
};

// --- Onboarding steps ---
export const ONBOARDING_STEPS = [
  {
    id: 'social',
    icon: 'Share2',
    labelKey: 'stepConnectSocial',
    descKey: 'stepConnectSocialDesc',
    route: '/settings?tab=social',
  },
  {
    id: 'vehicle',
    icon: 'Car',
    labelKey: 'stepAddVehicle',
    descKey: 'stepAddVehicleDesc',
    route: '/vehicles/add',
  },
  {
    id: 'marketplace',
    icon: 'Store',
    labelKey: 'stepListMarketplace',
    descKey: 'stepListMarketplaceDesc',
    route: '/marketplace/my-listings',
  },
  {
    id: 'vitrine',
    icon: 'Globe',
    labelKey: 'stepSetupVitrine',
    descKey: 'stepSetupVitrineDesc',
    route: '/settings?tab=vitrine',
  },
];

// --- Status config ---
export const OFFER_STATUS_CONFIG = {
  pending: { color: 'amber' },
  countered: { color: 'blue' },
  accepted: { color: 'green' },
  rejected: { color: 'red' },
  expired: { color: 'gray' },
  cancelled: { color: 'gray' },
};

export const APPOINTMENT_STATUS_CONFIG = {
  pending: { color: 'amber' },
  confirmed: { color: 'green' },
  completed: { color: 'gray' },
  rejected: { color: 'red' },
  cancelled: { color: 'gray' },
};

// --- Color map for status badges ---
export const STATUS_COLORS = {
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  gray: 'bg-gray-100 text-muted border-gray-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
};

// --- Event labels ---
export const EVENT_LABELS = {
  new_vehicle:    { label: 'Nouveau', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  price_reduction:{ label: 'Promo',   color: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/20'   },
  sold_vehicle:   { label: 'Vendu',   color: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/20'     },
};

// --- Re-exports for backward compat ---
export { formatPrice, formatMileage, formatAppointmentDate } from '../utils/formatters';
export { OFFER_STATUS_CONFIG as OFFER_STATUS_CONFIG_FULL, APPOINTMENT_STATUS_CONFIG as APPOINTMENT_STATUS_CONFIG_FULL } from './marketplace';

export const formatPastDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffHours < 1) return 'Il y a quelques minutes';
  if (diffHours < 24) return `Il y a ${Math.floor(diffHours)}h`;
  if (diffDays < 2) return 'Hier';
  return date.toLocaleDateString('fr-BE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

